import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Heart, Plus, X, Check, Edit2, Trash2, Loader2, FileText,
  AlertTriangle, Sparkles, Lock, ChevronDown, ChevronUp,
  ClipboardList, Target, Activity, Calendar, User, Lightbulb,
  Download
} from 'lucide-react';

// =====================================================
// TEMPLATE SELECTOR MODAL
// =====================================================

function TemplateSelector({ templates, onSelect, onClose, aiSuggestion }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(templates.map(t => t.category))];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-purple-900">Select Care Plan Template</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* AI Suggestion Banner */}
        {aiSuggestion && (
          <div className="mx-4 mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-purple-900">AI Suggestion</p>
              <p className="text-sm text-purple-700">{aiSuggestion}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b">
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto p-4">
          {categories.map(category => {
            const categoryTemplates = filteredTemplates.filter(t => t.category === category);
            if (categoryTemplates.length === 0) return null;
            
            return (
              <div key={category} className="mb-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">{category}</h4>
                <div className="space-y-2">
                  {categoryTemplates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{template.name}</div>
                      <div className="text-sm text-gray-500 mt-1">{template.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSelect(selectedTemplate)}
            disabled={!selectedTemplate}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Inject Template
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =====================================================
// CARE PLAN ENTRY EDITOR
// =====================================================

function CarePlanEntryEditor({ entry, template, onSave, onCancel, isNew }) {
  const [formData, setFormData] = useState({
    nursing_diagnosis: entry?.nursing_diagnosis || template?.default_diagnosis || '',
    short_term_goals: entry?.short_term_goals || template?.default_short_term_goals || [],
    long_term_goals: entry?.long_term_goals || template?.default_long_term_goals || [],
    interventions: entry?.interventions || template?.default_interventions || [],
    expected_outcomes: entry?.expected_outcomes || template?.default_outcomes || '',
    priority: entry?.priority || 1
  });
  const [loading, setLoading] = useState(false);

  // Parse JSON strings if needed
  useEffect(() => {
    const parsed = { ...formData };
    ['short_term_goals', 'long_term_goals', 'interventions'].forEach(field => {
      if (typeof parsed[field] === 'string') {
        try {
          parsed[field] = JSON.parse(parsed[field]);
        } catch {
          parsed[field] = [];
        }
      }
    });
    setFormData(parsed);
  }, []);

  const addGoal = (type) => {
    const key = `${type}_goals`;
    setFormData(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), { goal: '', target_date: '', met: false }]
    }));
  };

  const updateGoal = (type, index, field, value) => {
    const key = `${type}_goals`;
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].map((g, i) => i === index ? { ...g, [field]: value } : g)
    }));
  };

  const removeGoal = (type, index) => {
    const key = `${type}_goals`;
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index)
    }));
  };

  const addIntervention = () => {
    setFormData(prev => ({
      ...prev,
      interventions: [...(prev.interventions || []), { intervention: '', frequency: '', instructions: '' }]
    }));
  };

  const updateIntervention = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      interventions: prev.interventions.map((i, idx) => idx === index ? { ...i, [field]: value } : i)
    }));
  };

  const removeIntervention = (index) => {
    setFormData(prev => ({
      ...prev,
      interventions: prev.interventions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nursing_diagnosis || !formData.interventions?.length) {
      toast.error('Nursing diagnosis and at least one intervention are required');
      return;
    }
    setLoading(true);
    try {
      await onSave({
        ...formData,
        template_id: template?.id || entry?.template_id,
        template_name: template?.name || entry?.template_name
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 border-2 border-purple-200">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-purple-900 flex items-center gap-2">
          {template?.name || entry?.template_name || 'Custom Problem'}
          {isNew && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">New</span>}
        </h4>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        {/* Nursing Diagnosis */}
        <div>
          <Label className="text-sm font-medium">Nursing Diagnosis / Problem *</Label>
          <Textarea
            value={formData.nursing_diagnosis}
            onChange={(e) => setFormData(prev => ({ ...prev, nursing_diagnosis: e.target.value }))}
            placeholder="Risk for... / Impaired... / Ineffective..."
            rows={2}
            className="mt-1"
          />
        </div>

        {/* Short-term Goals */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Short-term Goals</Label>
            <Button variant="ghost" size="sm" onClick={() => addGoal('short_term')}>
              <Plus className="w-4 h-4 mr-1" /> Add Goal
            </Button>
          </div>
          {(formData.short_term_goals || []).map((goal, idx) => (
            <div key={idx} className="flex gap-2 mb-2 items-start">
              <Checkbox
                checked={goal.met}
                onCheckedChange={(checked) => updateGoal('short_term', idx, 'met', checked)}
                className="mt-3"
              />
              <div className="flex-1">
                <Textarea
                  value={goal.goal}
                  onChange={(e) => updateGoal('short_term', idx, 'goal', e.target.value)}
                  placeholder="Goal description..."
                  rows={1}
                  className="text-sm"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeGoal('short_term', idx)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>

        {/* Long-term Goals */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Long-term Goals</Label>
            <Button variant="ghost" size="sm" onClick={() => addGoal('long_term')}>
              <Plus className="w-4 h-4 mr-1" /> Add Goal
            </Button>
          </div>
          {(formData.long_term_goals || []).map((goal, idx) => (
            <div key={idx} className="flex gap-2 mb-2 items-start">
              <Checkbox
                checked={goal.met}
                onCheckedChange={(checked) => updateGoal('long_term', idx, 'met', checked)}
                className="mt-3"
              />
              <div className="flex-1">
                <Textarea
                  value={goal.goal}
                  onChange={(e) => updateGoal('long_term', idx, 'goal', e.target.value)}
                  placeholder="Goal description..."
                  rows={1}
                  className="text-sm"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeGoal('long_term', idx)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>

        {/* Interventions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Interventions * (Visible to Caregivers)</Label>
            <Button variant="ghost" size="sm" onClick={addIntervention}>
              <Plus className="w-4 h-4 mr-1" /> Add Intervention
            </Button>
          </div>
          {(formData.interventions || []).map((intervention, idx) => (
            <div key={idx} className="p-3 bg-gray-50 rounded-lg mb-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500">Intervention {idx + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeIntervention(idx)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <Textarea
                value={intervention.intervention}
                onChange={(e) => updateIntervention(idx, 'intervention', e.target.value)}
                placeholder="What should be done..."
                rows={2}
                className="mb-2 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Frequency</Label>
                  <Input
                    value={intervention.frequency}
                    onChange={(e) => updateIntervention(idx, 'frequency', e.target.value)}
                    placeholder="Every visit, Daily, etc."
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Instructions</Label>
                  <Input
                    value={intervention.instructions}
                    onChange={(e) => updateIntervention(idx, 'instructions', e.target.value)}
                    placeholder="Additional details..."
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Expected Outcomes */}
        <div>
          <Label className="text-sm font-medium">Expected Outcomes</Label>
          <Textarea
            value={formData.expected_outcomes}
            onChange={(e) => setFormData(prev => ({ ...prev, expected_outcomes: e.target.value }))}
            placeholder="Client will..."
            rows={2}
            className="mt-1"
          />
        </div>

        {/* Priority */}
        <div>
          <Label className="text-sm font-medium">Priority</Label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
            className="w-full mt-1 px-3 py-2 border rounded-md"
          >
            <option value={1}>1 - Highest Priority</option>
            <option value={2}>2 - High Priority</option>
            <option value={3}>3 - Medium Priority</option>
            <option value={4}>4 - Low Priority</option>
          </select>
        </div>
      </div>

      {/* Save/Cancel */}
      <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          {isNew ? 'Add to Care Plan' : 'Save Changes'}
        </Button>
      </div>
    </Card>
  );
}

// =====================================================
// SIGNATURE MODAL
// =====================================================

function SignatureModal({ onSign, onClose, rnName }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = () => {
    if (!hasSignature) {
      toast.error('Please provide your signature');
      return;
    }
    const signatureData = canvasRef.current.toDataURL('image/png');
    onSign(signatureData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-purple-900 mb-4">Sign & Lock Care Plan</h3>
        
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">This action cannot be undone</p>
              <p>Once signed, this care plan will be locked and cannot be modified. A new version must be created for changes.</p>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <Label className="text-sm font-medium mb-2 block">RN Signature: {rnName}</Label>
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            className="border-2 border-gray-300 rounded-lg cursor-crosshair bg-white w-full"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          <Button variant="ghost" size="sm" onClick={clearSignature} className="mt-2">
            Clear Signature
          </Button>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSign}
            disabled={!hasSignature}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Lock className="w-4 h-4 mr-2" />
            Sign & Lock Care Plan
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =====================================================
// MAIN CARE PLAN SECTION COMPONENT
// =====================================================

export default function CarePlanSection({ clientId }) {
  const { user } = useAuth();
  const [carePlan, setCarePlan] = useState(null);
  const [entries, setEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntryTemplate, setNewEntryTemplate] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [setupRequired, setSetupRequired] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const canManage = user && ['rn', 'super_admin'].includes(user.role);
  const isSigned = carePlan?.status === 'signed';

  const fetchCarePlan = async () => {
    setLoading(true);
    setSetupRequired(false);
    try {
      const [planResponse, templateResponse] = await Promise.all([
        api.get(`/emr/care-plan/client/${clientId}`),
        api.get('/emr/care-plan/templates')
      ]);
      
      setCarePlan(planResponse.data.care_plan);
      
      // Parse JSON fields in entries
      const parsedEntries = (planResponse.data.entries || []).map(entry => ({
        ...entry,
        short_term_goals: typeof entry.short_term_goals === 'string' ? JSON.parse(entry.short_term_goals || '[]') : entry.short_term_goals || [],
        long_term_goals: typeof entry.long_term_goals === 'string' ? JSON.parse(entry.long_term_goals || '[]') : entry.long_term_goals || [],
        interventions: typeof entry.interventions === 'string' ? JSON.parse(entry.interventions || '[]') : entry.interventions || []
      }));
      setEntries(parsedEntries);
      
      setTemplates(templateResponse.data.templates || []);
      
      // Expand all entries by default
      const expanded = {};
      parsedEntries.forEach(e => { expanded[e.id] = true; });
      setExpandedEntries(expanded);
      
    } catch (error) {
      console.error('Failed to fetch care plan:', error);
      if (error.response?.status === 503) {
        setSetupRequired(true);
      } else {
        toast.error('Failed to load care plan');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchCarePlan();
    }
  }, [clientId]);

  const handleTemplateSelect = async (template) => {
    setShowTemplateSelector(false);
    setNewEntryTemplate(template);
  };

  const handleSaveEntry = async (entryData) => {
    try {
      if (editingEntry) {
        await api.put(`/emr/care-plan/client/${clientId}/entry/${editingEntry.id}`, entryData);
        toast.success('Care plan entry updated');
      } else {
        await api.post(`/emr/care-plan/client/${clientId}/entry`, entryData);
        toast.success('Added to care plan');
      }
      setEditingEntry(null);
      setNewEntryTemplate(null);
      fetchCarePlan();
    } catch (error) {
      console.error('Save entry error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm('Remove this problem from the care plan?')) return;
    try {
      await api.delete(`/emr/care-plan/client/${clientId}/entry/${entryId}`);
      toast.success('Entry removed');
      fetchCarePlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const handleSign = async (signatureData) => {
    try {
      await api.post(`/emr/care-plan/client/${clientId}/sign`, { signature_data: signatureData });
      toast.success('Care plan signed and locked');
      setShowSignature(false);
      fetchCarePlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign care plan');
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.get(`/emr/care-plan/client/${clientId}/export-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CarePlan_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Care Plan PDF downloaded');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const toggleEntry = (entryId) => {
    setExpandedEntries(prev => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (setupRequired) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
        <h3 className="font-semibold text-gray-900 text-lg">Care Plan Setup Required</h3>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          The Care Plan tables need to be created in your database.
        </p>
        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-left max-w-lg mx-auto">
          <p className="text-sm font-medium text-gray-700 mb-2">To enable Care Plans:</p>
          <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
            <li>Open your Supabase SQL Editor</li>
            <li>Copy the contents of <code className="bg-gray-200 px-1 rounded">/app/backend/sql/care_plan_tables.sql</code></li>
            <li>Execute the SQL script</li>
            <li>Refresh this page</li>
          </ol>
        </div>
        <Button onClick={fetchCarePlan} variant="outline" className="mt-4">
          <Loader2 className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="care-plan-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Heart className="w-5 h-5 text-purple-600" />
            Care Plan
            {isSigned && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Lock className="w-3 h-3" /> Signed
              </span>
            )}
          </h3>
          {carePlan?.updated_by_name && (
            <p className="text-xs text-gray-500 mt-1">
              Last updated by {carePlan.updated_by_name} on {new Date(carePlan.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
        
        {canManage && !isSigned && (
          <div className="flex gap-2">
            <Button
              onClick={() => setShowTemplateSelector(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="add-template-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add from Template
            </Button>
            {entries.length > 0 && (
              <>
                <Button
                  onClick={handleExportPdf}
                  variant="outline"
                  disabled={exportingPdf}
                  data-testid="export-care-plan-pdf-btn"
                >
                  {exportingPdf ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export PDF
                </Button>
                <Button
                  onClick={() => setShowSignature(true)}
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                  data-testid="sign-care-plan-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Sign & Lock
                </Button>
              </>
            )}
          </div>
        )}
        
        {/* Show Export PDF for signed plans (view-only users too) */}
        {isSigned && entries.length > 0 && (
          <Button
            onClick={handleExportPdf}
            variant="outline"
            disabled={exportingPdf}
            data-testid="export-care-plan-pdf-btn"
          >
            {exportingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
        )}
      </div>

      {/* Signed Care Plan Info */}
      {isSigned && carePlan?.signed_by_name && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Care Plan Signed & Locked</p>
                <p className="text-sm text-green-700">
                  Signed by {carePlan.signed_by_name} on {new Date(carePlan.signed_at).toLocaleString()}
                </p>
              </div>
            </div>
            {carePlan.signature_data && (
              <img src={carePlan.signature_data} alt="RN Signature" className="h-12 border rounded bg-white p-1" />
            )}
          </div>
        </Card>
      )}

      {/* New Entry Editor */}
      {newEntryTemplate && (
        <CarePlanEntryEditor
          template={newEntryTemplate}
          isNew={true}
          onSave={handleSaveEntry}
          onCancel={() => setNewEntryTemplate(null)}
        />
      )}

      {/* Editing Entry */}
      {editingEntry && (
        <CarePlanEntryEditor
          entry={editingEntry}
          isNew={false}
          onSave={handleSaveEntry}
          onCancel={() => setEditingEntry(null)}
        />
      )}

      {/* Care Plan Entries */}
      {entries.length === 0 && !newEntryTemplate ? (
        <Card className="p-8 text-center">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No care plan entries yet</p>
          {canManage && (
            <Button
              onClick={() => setShowTemplateSelector(true)}
              variant="outline"
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Care Plan
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {entries.filter(e => e.is_active !== false).map((entry, idx) => (
            <Card key={entry.id} className="overflow-hidden">
              {/* Entry Header */}
              <button
                onClick={() => toggleEntry(entry.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    entry.priority === 1 ? 'bg-red-100 text-red-700' :
                    entry.priority === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {entry.priority}
                  </span>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">
                      {entry.template_name || 'Custom Problem'}
                    </h4>
                    <p className="text-sm text-gray-500 truncate max-w-md">
                      {entry.nursing_diagnosis?.substring(0, 80)}...
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {entry.interventions?.length || 0} interventions
                  </span>
                  {expandedEntries[entry.id] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Entry Content (Expanded) */}
              {expandedEntries[entry.id] && (
                <div className="px-4 pb-4 border-t">
                  {/* Nursing Diagnosis */}
                  <div className="mt-4">
                    <Label className="text-xs text-gray-500">Nursing Diagnosis / Problem</Label>
                    <p className="text-sm text-gray-900 mt-1">{entry.nursing_diagnosis}</p>
                  </div>

                  {/* Goals */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-xs text-gray-500 flex items-center gap-1">
                        <Target className="w-3 h-3" /> Short-term Goals
                      </Label>
                      {entry.short_term_goals?.length > 0 ? (
                        <ul className="mt-1 space-y-1">
                          {entry.short_term_goals.map((g, i) => (
                            <li key={i} className={`text-sm flex items-start gap-2 ${g.met ? 'text-green-600' : 'text-gray-700'}`}>
                              {g.met ? <Check className="w-4 h-4 mt-0.5" /> : <span className="w-4 h-4 mt-0.5 rounded-full border-2" />}
                              {g.goal}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 mt-1">No short-term goals</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500 flex items-center gap-1">
                        <Target className="w-3 h-3" /> Long-term Goals
                      </Label>
                      {entry.long_term_goals?.length > 0 ? (
                        <ul className="mt-1 space-y-1">
                          {entry.long_term_goals.map((g, i) => (
                            <li key={i} className={`text-sm flex items-start gap-2 ${g.met ? 'text-green-600' : 'text-gray-700'}`}>
                              {g.met ? <Check className="w-4 h-4 mt-0.5" /> : <span className="w-4 h-4 mt-0.5 rounded-full border-2" />}
                              {g.goal}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 mt-1">No long-term goals</p>
                      )}
                    </div>
                  </div>

                  {/* Interventions */}
                  <div className="mt-4">
                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                      <Activity className="w-3 h-3" /> Interventions (Caregiver Guide)
                    </Label>
                    <div className="mt-2 space-y-2">
                      {entry.interventions?.map((intervention, i) => (
                        <div key={i} className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-sm font-medium text-purple-900">{intervention.intervention}</p>
                          <div className="flex gap-4 mt-1">
                            <span className="text-xs text-purple-700">
                              <strong>Frequency:</strong> {intervention.frequency || 'As needed'}
                            </span>
                            {intervention.instructions && (
                              <span className="text-xs text-purple-700">
                                <strong>Instructions:</strong> {intervention.instructions}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Expected Outcomes */}
                  {entry.expected_outcomes && (
                    <div className="mt-4">
                      <Label className="text-xs text-gray-500">Expected Outcomes</Label>
                      <p className="text-sm text-gray-700 mt-1">{entry.expected_outcomes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  {canManage && !isSigned && (
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingEntry(entry)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <TemplateSelector
          templates={templates}
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {/* Signature Modal */}
      {showSignature && (
        <SignatureModal
          rnName={user?.full_name}
          onSign={handleSign}
          onClose={() => setShowSignature(false)}
        />
      )}
    </div>
  );
}
